import PropTypes from 'prop-types';
import React, { useId } from 'react';

// Wrapper every field goes through (see FORMS.md). It owns the label, the
// required marker, the hint / error line and the ids that tie them to the
// control, so every field on every screen reads and behaves the same.
//
//   editable    default
//   readonly    value visible and selectable, cannot be changed by the user
//   disabled    not applicable right now; skipped by keyboard and submit
//   calculated  derived by the system (formula); never typed by the user

export const FIELD_MODES = ['editable', 'readonly', 'disabled', 'calculated'];

export const isLocked = mode => mode !== 'editable';

const SPAN_CLASS = {
  full: 'hy-field--full',
  2: 'hy-field--span-2',
  3: 'hy-field--span-3',
};

const cleanId = raw => `hy-${String(raw).replace(/[^a-zA-Z0-9_-]/g, '')}`;

const FormField = ({
  label,
  name,
  id: idProp,
  mode = 'editable',
  required = false,
  hint,
  error,
  span,
  group = false,
  className = '',
  children,
}) => {
  const autoId = useId();
  const id = idProp || cleanId(autoId);
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const labelId = `${id}-label`;

  const invalid = Boolean(error) && mode === 'editable';
  // An error replaces the hint so the line under the field never grows twice.
  const message = invalid ? error : hint;
  const messageId = invalid ? errorId : hintId;
  const showRequired = required && mode === 'editable';

  const controlProps = {
    id,
    name,
    invalid,
    required: showRequired,
    readOnly: mode === 'readonly' || mode === 'calculated',
    disabled: mode === 'disabled',
    'aria-invalid': invalid || undefined,
    'aria-required': showRequired || undefined,
    'aria-describedby': message ? messageId : undefined,
  };

  const labelContent = (
    <>
      {label}
      {showRequired && (
        <>
          <span className='hy-field__req' aria-hidden='true'>
            *
          </span>
          <span className='visually-hidden'> (required)</span>
        </>
      )}
      {mode === 'calculated' && (
        <span className='hy-field__tag' title='Calculated by the system'>
          <i className='ri-function-line' aria-hidden='true' />
          Auto
        </span>
      )}
      {mode === 'readonly' && (
        <i
          className='ri-lock-line hy-field__lock'
          title='Read-only'
          role='img'
          aria-label='Read-only'
        />
      )}
    </>
  );

  const classes = [
    'hy-field',
    `hy-field--${mode}`,
    invalid && 'hy-field--invalid',
    SPAN_CLASS[span],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={classes}
      data-field={name}
      role={group ? 'group' : undefined}
      aria-labelledby={group && label ? labelId : undefined}
    >
      {label &&
        (group ? (
          <span id={labelId} className='hy-field__label'>
            {labelContent}
          </span>
        ) : (
          <label htmlFor={id} className='hy-field__label'>
            {labelContent}
          </label>
        ))}
      {typeof children === 'function' ? children(controlProps) : children}
      {message && (
        <div
          id={messageId}
          className={invalid ? 'hy-field__error' : 'hy-field__hint'}
          role={invalid ? 'alert' : undefined}
        >
          {invalid && (
            <i className='ri-error-warning-line' aria-hidden='true' />
          )}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
};

FormField.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string,
  id: PropTypes.string,
  mode: PropTypes.oneOf(FIELD_MODES),
  required: PropTypes.bool,
  hint: PropTypes.node,
  error: PropTypes.node,
  span: PropTypes.oneOf(['full', 2, 3]),
  group: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

export default FormField;
